import React from 'react';
import { vacancyApi } from '@cp/entities/vacancy';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { Button } from '@cp/ds/src/components/button';
import { DeleteIcon } from '@cp/ds/src/icons';
import { useDispatch } from 'react-redux';
import { ThunkDispatch } from '@reduxjs/toolkit';
import { ApiServerEntityIDType } from '@common/types';
import { ClientCatalogItemViewModel } from '@common/model/client/catalog/item/clientCatalogItemViewModel';
import { format } from 'date-fns';
import { CityViewModel } from '@common/model/location/cityViewModel';
import { AppFormProvider } from '@cp/utils/form';
import { DeepPartial } from 'react-hook-form';
import { VacancyForm, VacancyFormProps } from '../form';
import { Header } from '../header';
import { contentContanerSx } from '../../lib';
import { FormModel, VACANCY_EDIT_DATE_FORMAT, useVacancyForm } from '../../model';

const mapViewModelToFormValues = (viewModel: ClientCatalogItemViewModel): DeepPartial<FormModel> => {
  const { careerId, startedAt, endedAt, children, city, childWithDisabilities, regularSpecialist, translation, location, address } =
    viewModel;

  return {
    careerId,
    date: startedAt ? format(new Date(startedAt), VACANCY_EDIT_DATE_FORMAT) : undefined,
    startedAt,
    endedAt,
    city: city as CityViewModel,
    address: { address, location },
    children,
    translations: [{ title: translation?.title, languageCode: translation?.languageCode, description: translation?.description }],
    childWithDisabilities,
    regularSpecialist,
  };
};

export interface ExistedProps {
  id: ApiServerEntityIDType;
  onClose: () => void;
}

export const Existed: React.FC<ExistedProps> = ({ id, onClose }) => {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const { data, isLoading: isLoadingVacancy } = vacancyApi.endpoints.getVacancyForCurrentClientById.useQuery({ id });

  const isLoading = isLoadingVacancy;

  const handleSubmit = React.useCallback<VacancyFormProps['onSubmit']>(
    (values) => {
      return dispatch(vacancyApi.endpoints.updateVacancyForCurrentClientById.initiate({ params: { id }, body: values }));
    },
    [dispatch, id],
  );
  const defaultValues = React.useMemo(() => data && mapViewModelToFormValues(data), [data]);
  const form = useVacancyForm(defaultValues);

  const handleDelete = React.useCallback(async () => {
    await dispatch(vacancyApi.endpoints.deleteVacancyForCurrentClientById.initiate({ params: { id } }));
    onClose();
  }, [dispatch, id, onClose]);

  if (isLoading) {
    return <ContentSkeleton blocks={2} />;
  }

  if (!defaultValues) {
    return null;
  }

  return (
    <AppFormProvider form={form}>
      <Header
        careerId={defaultValues.careerId || 1}
        endAdornment={
          <Button onClick={handleDelete} sx={{ ml: 'auto', mr: 0 }} variant="text">
            <DeleteIcon fontSize="1.5rem" />
          </Button>
        }
        onGoBack={onClose}
      />
      <VacancyForm
        contentContainerSx={contentContanerSx}
        onComplete={onClose}
        onSecondaryButtonClick={handleDelete}
        onSubmit={handleSubmit}
        secondaryButtonTitle="Удалить"
        submitButtonTitle="Сохранить"
      />
    </AppFormProvider>
  );
};
